<!-- eslint-disable max-len -->
<template>
  <div>
    <div class="mb-3 inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700">
      <svg
        viewBox="0 0 24 24"
        class="size-3.5"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <rect
          x="2"
          y="2"
          width="20"
          height="20"
          rx="6"
        />
        <circle
          cx="12"
          cy="12"
          r="4"
        />
        <circle
          cx="17.5"
          cy="6.5"
          r="1.3"
          fill="currentColor"
          stroke="none"
        />
      </svg>
      {{ t('livvia.videoPicker.accountChip', { handle: accountHandle }) }}
    </div>

    <!-- Loading skeleton -->
    <div
      v-if="loading"
      class="grid grid-cols-2 gap-3 sm:grid-cols-3"
    >
      <div
        v-for="i in 6"
        :key="i"
        class="aspect-square animate-pulse rounded-2xl bg-gray-200"
      />
    </div>

    <!-- Error state -->
    <div
      v-else-if="error"
      class="rounded-2xl border border-dashed border-red-200 bg-red-50 px-6 py-8 text-center"
    >
      <ExclamationTriangleIcon class="mx-auto mb-2 size-6 text-red-500" />
      <p class="text-sm font-semibold text-red-800">
        {{ t('livvia.videoPicker.errorTitle') }}
      </p>
      <p class="mt-1 text-xs text-red-700/80">
        {{ error }}
      </p>
      <button
        type="button"
        class="mt-3 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100"
        @click="loadMedias"
      >
        {{ t('livvia.videoPicker.retry') }}
      </button>
    </div>

    <!-- Empty state -->
    <div
      v-else-if="!videos.length"
      class="rounded-2xl border border-dashed border-gray-300 bg-white/40 px-6 py-8 text-center"
    >
      <VideoCameraSlashIcon class="mx-auto mb-2 size-6 text-gray-400" />
      <p class="text-sm font-semibold text-gray-600">
        {{ t('livvia.videoPicker.emptyTitle') }}
      </p>
      <p class="mt-1 text-xs text-gray-500">
        {{ t('livvia.videoPicker.emptySubtitle') }}
      </p>
    </div>

    <!-- Grid -->
    <div
      v-else
      class="grid grid-cols-2 gap-3 sm:grid-cols-3"
    >
      <button
        v-for="(v, i) in videos"
        :key="v.id"
        type="button"
        class="group relative text-left"
        @click="selectedId = v.id"
      >
        <span
          v-if="v.id === livvia.lastLive?.mediaId"
          class="absolute -top-2 left-2 z-10 rounded-full bg-teal-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white shadow-md"
        >
          {{ t('livvia.videoPicker.flagLatest') }}
        </span>
        <div
          class="relative aspect-square overflow-hidden rounded-2xl border-2 shadow-sm transition"
          :class="[
            selectedId === v.id
              ? 'border-teal-500 ring-2 ring-teal-200'
              : 'border-transparent',
            v.thumbnail_url ? '' : fallbackGradient(i),
          ]"
        >
          <img
            v-if="v.thumbnail_url"
            :src="v.thumbnail_url"
            :alt="v.caption || v.id"
            class="absolute inset-0 h-full w-full object-cover"
            loading="lazy"
          >

          <span class="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />

          <span
            v-if="isVideoMedia(v)"
            class="absolute left-2 top-2 z-10 inline-flex items-center gap-1 rounded-md bg-red-600/95 px-1.5 py-0.5 text-[9px] font-bold tracking-wide text-white"
          >
            <span class="size-1 rounded-full bg-white" />
            {{ t('livvia.videoPicker.liveTag') }}
          </span>

          <span class="absolute left-1/2 top-1/2 z-10 flex size-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/90">
            <PlayIcon class="ml-0.5 size-3.5 text-gray-900" />
          </span>

          <span class="absolute left-2 bottom-1.5 z-10 truncate text-[11px] font-bold text-white drop-shadow">
            {{ formatDate(v.timestamp) }}
          </span>

          <span
            v-if="selectedId === v.id"
            class="absolute right-1.5 top-1.5 z-20 flex size-5 items-center justify-center rounded-full bg-teal-600 text-white shadow-md"
          >
            <CheckIcon class="size-3" />
          </span>
        </div>
      </button>
    </div>

    <div class="mt-4 flex flex-wrap items-center gap-4">
      <button
        type="button"
        class="inline-flex items-center gap-2 rounded-xl bg-teal-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 disabled:cursor-not-allowed disabled:opacity-60"
        :disabled="!selectedId || loading"
        @click="onGenerate"
      >
        <SparklesIcon class="size-4" />
        {{ t('livvia.videoPicker.actionGenerate') }}
      </button>
      <button
        type="button"
        class="text-sm font-semibold text-gray-500 underline underline-offset-2 hover:text-gray-700"
        @click="onBack"
      >
        {{ t('livvia.videoPicker.actionBack') }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  computed, onMounted, ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import {
  PlayIcon, SparklesIcon, CheckIcon,
} from '@heroicons/vue/24/solid';
import { ExclamationTriangleIcon, VideoCameraSlashIcon } from '@heroicons/vue/24/outline';
import useAuthStore from '@/store/auth';
import useLivviaStore from '@/store/livvia';
import usePluginStore from '@/store/plugin/plugin';
import { InstagramMedia, isVideoMedia } from '@/models/plugin/transcription';

const { t } = useI18n();
const { session } = useAuthStore();
const livvia = useLivviaStore();
const { pluginGetInstagramMedias } = usePluginStore();

const accountHandle = computed(() => {
  const name = session?.name?.split(' ')[0] || 'voce';
  return name.toLowerCase().replace(/[^a-z0-9]/g, '');
});

const videos = ref<InstagramMedia[]>([]);
const selectedId = ref<string>('');
const loading = ref(true);
const error = ref('');

const fallbackGradient = (i: number): string => {
  const palettes = [
    'bg-gradient-to-br from-orange-300 via-pink-500 to-purple-600',
    'bg-gradient-to-br from-sky-400 via-indigo-500 to-purple-600',
    'bg-gradient-to-br from-emerald-300 via-sky-400 to-indigo-500',
    'bg-gradient-to-br from-teal-400 via-sky-500 to-indigo-500',
    'bg-gradient-to-br from-amber-300 via-pink-400 to-purple-500',
    'bg-gradient-to-br from-emerald-200 via-sky-300 to-indigo-400',
  ];
  return palettes[i % palettes.length];
};

const formatDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleDateString('pt-BR', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '';
  }
};

const loadMedias = async () => {
  const pluginId = livvia.lastLive?.pluginId;
  if (!pluginId) {
    error.value = t('livvia.videoPicker.noPluginError');
    loading.value = false;
    return;
  }
  loading.value = true;
  error.value = '';
  try {
    const resp = await pluginGetInstagramMedias({ pluginId, page: 1, limit: 12 });
    videos.value = resp.data || [];
    // Pre-select the "pending" media if it's in the list, else the first video.
    const preferredId = livvia.lastLive?.mediaId;
    const preferred = preferredId ? videos.value.find((v) => v.id === preferredId) : null;
    selectedId.value = preferred?.id || videos.value[0]?.id || '';
  } catch (e: any) {
    error.value = e?.message || t('livvia.videoPicker.errorSubtitle');
  } finally {
    loading.value = false;
  }
};

const onGenerate = () => {
  const mediaId = selectedId.value;
  const pluginId = livvia.lastLive?.pluginId;
  if (!mediaId || !pluginId) return;
  livvia.startProcessing({ pluginId, mediaId });
};

const onBack = () => {
  if (livvia.lastLive) {
    livvia.enterPostLive(livvia.lastLive);
  } else {
    livvia.reset();
  }
};

onMounted(loadMedias);
</script>
